<template>
  <CollapsibleRoot
    v-if="isExpandable"
    v-model:open="isExpanded"
    class="PassengerItem"
    :class="{ 'is-selected': selected, 'is-disabled': disabled }"
  >
    <div class="PassengerItem-main">
      <PersonIdentity
        :name="passengerName"
        :description="passengerTypeLabel"
        :active="selected"
        :disabled
      >
        <template v-if="creditsLabel" #extra>
          <Tag
            type="neutral"
            variant="subtle"
            size="xs"
            class="PassengerItem-credits"
          >
            {{ creditsLabel }}
          </Tag>
        </template>
      </PersonIdentity>

      <CollapsibleTrigger asChild>
        <ActionButton
          type="button"
          class="PassengerItem-actionIcon"
          :leftIcon="isExpanded ? 'chevron-up' : 'chevron-down'"
          variant="secondary"
          size="sm"
          :disabled
          :aria-label="t('ui.booking.search.passengers.seat.toggleAriaLabel', { name: passengerName })"
        />
      </CollapsibleTrigger>
    </div>

    <CollapsibleContent class="PassengerItem-expandedWrapper">
      <div class="PassengerItem-expanded">
        <RadioButtons
          class="PassengerItem-seatSelection"
          :modelValue="seatSelection ?? defaultSeatOption"
          :options="seatOptions"
          :disabled
          :ariaLabel="t('ui.booking.search.passengers.seat.ariaLabel', { name: passengerName })"
          @update:model-value="handleSeatChange"
        />

        <ActionButton
          type="button"
          class="PassengerItem-toggleButton"
          variant="primary"
          size="sm"
          :disabled
          @click="handleToggleClick"
        >
          {{ selected
            ? t('ui.booking.search.passengers.removePassenger', { name: passengerName })
            : t('ui.booking.search.passengers.addPassenger', { name: passengerName }) }}
        </ActionButton>
      </div>
    </CollapsibleContent>
  </CollapsibleRoot>

  <div
    v-else
    class="PassengerItem"
    :class="{ 'is-selected': selected, 'is-disabled': disabled }"
  >
    <div class="PassengerItem-main">
      <PersonIdentity
        :name="passengerName"
        :description="passengerTypeLabel"
        :active="selected"
        :disabled
      >
        <template v-if="creditsLabel" #extra>
          <Tag
            type="neutral"
            variant="subtle"
            size="xs"
            class="PassengerItem-credits"
          >
            {{ creditsLabel }}
          </Tag>
        </template>
      </PersonIdentity>

      <ActionButton
        type="button"
        class="PassengerItem-actionIcon"
        :leftIcon="actionIcon"
        variant="secondary"
        size="sm"
        :disabled
        :aria-label="actionLabel"
        @click="handleActionClick"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import { CollapsibleRoot, CollapsibleTrigger, CollapsibleContent } from 'reka-ui';
import { getPersonFullName, getSeatOptions, getDefaultSeatOption, getPassengerCredits, type SeatType } from '@cvo/domain';
import ActionButton from '../../atoms/ActionButton/ActionButton.vue';
import Tag from '../../atoms/Tag/Tag.vue';
import RadioButtons from '../../atoms/RadioButtons/RadioButtons.vue';
import type { RadioOption } from '../../atoms/RadioButtons/RadioButtons.model';
import PersonIdentity from '../PersonIdentity/PersonIdentity.vue';
import type { PassengerItemProps } from './PassengerItem.model';

const props = defineProps<PassengerItemProps>();

const emit = defineEmits<{
  toggle: [];
}>();

const seatSelection = defineModel<SeatType>('seatSelection');

const { t } = useI18n();

const isExpanded = ref(false);

const passengerName = computed(() => getPersonFullName(props.passenger.person));
const passengerTypeLabel = computed(() => t(`ui.global.passengers.type.${props.passenger.passengerType}`));

// The seat types the API offers this passenger, in the order it returns them.
const rawSeatOptions = computed(() => getSeatOptions(props.passenger));

// Translation keys match the API's own SeatType values ('LAP' | 'SEAT') directly.
const seatOptions = computed<RadioOption[]>(() => rawSeatOptions.value.map((seatType) => {
  const key = `ui.booking.search.passengers.seat.${seatType}`;
  return { label: t(`${key}.label`), value: seatType, description: t(`${key}.description`) };
}));

// Even a single offered option must stay visible and pre-selected, so the row expands as soon as
// there is any choice to show.
const isExpandable = computed(() => props.showSeatSelection && rawSeatOptions.value.length > 0);

/** LAP when offered, else the only/first offered option, applied until the user picks another. */
const defaultSeatOption = computed(() => getDefaultSeatOption(rawSeatOptions.value));

const selectedSeatOption = computed(() => (isExpandable.value ? seatSelection.value ?? defaultSeatOption.value : undefined));

const creditsLabel = computed(() => {
  const credits = getPassengerCredits(props.passenger, props.tripType, selectedSeatOption.value);
  return credits === undefined ? undefined : t('ui.booking.search.passengers.credits', credits);
});

const actionIcon = computed(() => (props.selected ? 'window-close' : 'plus'));

// The action button is the row's only tab stop, so it carries the whole row's accessible name.
const actionLabel = computed(() => (props.selected
  ? t('ui.booking.search.passengers.removePassenger', { name: passengerName.value })
  : t('ui.booking.search.passengers.addPassenger', { name: passengerName.value })));

function handleSeatChange(value: string | null | undefined) {
  if (!value) return;
  seatSelection.value = value as SeatType;
}

function handleActionClick() {
  emit('toggle');
}

// The full-width button, once expanded, is the only way that row adds/removes itself.
function handleToggleClick() {
  handleActionClick();
  isExpanded.value = false;
}
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';

@keyframes passengerItemSlideDown {
  from { height: 0; }
  to { height: var(--reka-collapsible-content-height); }
}

@keyframes passengerItemSlideUp {
  from { height: var(--reka-collapsible-content-height); }
  to { height: 0; }
}

@layer cvo-ui {
  .PassengerItem {
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-2;
    width: 100%;
    padding: vars.$atoms-autocomplete-baseitem-padding-top-bottom vars.$atoms-autocomplete-baseitem-padding-left-right;
    background-color: vars.$atoms-autocomplete-baseitem-default-bg-color;
    border: none;
    border-radius: vars.$atoms-autocomplete-baseitem-border-radius;
    transition: background-color 0.2s ease;
    text-align: start;

    // The row itself is never clickable — only the action button(s) inside it are.
    &.is-disabled {
      cursor: not-allowed;
      background-color: vars.$atoms-autocomplete-baseitem-disabled-bg-color;
    }

    .PersonIdentity-name {
      @include text.text('sm', vars.$typography-weight-strong);
      color: vars.$atoms-autocomplete-baseitem-default-label-color;
    }

    &.is-selected .PersonIdentity-name {
      color: vars.$atoms-autocomplete-baseitem-active-label-color;
    }

    .PersonIdentity-description {
      @include text.text('xs');
      color: vars.$atoms-autocomplete-baseitem-default-secondarytext-color;
    }

    &.is-selected .PersonIdentity-description {
      color: vars.$atoms-autocomplete-baseitem-active-secondarytext-color;
    }

    &-main {
      display: flex;
      align-items: center;
      gap: vars.$dimensions-d-2;
      width: 100%;
    }

    &-credits {
      min-width: 0;
      height: auto;
      padding-block: vars.$dimensions-d-0-5;
      padding-inline: vars.$dimensions-d-1;
    }

    & &-actionIcon {
      min-width: 0;
      .Icon {
        transition: transform 150ms ease-in;
      }

      &:hover, &:focus {
        .Icon {
          transform: scale(1.4);
        }
      }
    }

    &-expandedWrapper {
      overflow: hidden;

      &[data-state='open'] {
        animation: passengerItemSlideDown vars.$transition-base ease-out;
      }

      &[data-state='closed'] {
        animation: passengerItemSlideUp vars.$transition-base ease-in;
      }
    }

    &-expanded {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-2;
      width: 100%;
      padding-block-start: vars.$dimensions-d-2;
    }

    &-toggleButton {
      width: 100%;
      justify-content: center;
    }

    &-seatSelection {
      width: 100%;

      // Kinda "RadioCard" variant. Only used here for the moment, not worth extracting a full component.
      .RadioButtons {
        display: flex;
        flex-wrap: wrap;
        gap: vars.$dimensions-d-2;
        width: 100%;

        &-item {
          display: flex;
          flex: 1 1 0;
          flex-direction: column;
          align-items: flex-start;
          min-width: 120px;
          padding: vars.$dimensions-d-3;
          border-radius: vars.$border-radius-sm;
          border: 1px solid vars.$color-neutral-gray-300;
          transition: border-color vars.$transition-fast ease;

          &:focus-visible {
            outline: 2px solid vars.$color-brand-focus;
            outline-offset: 2px;

            .RadioButtons-circle {
              outline: none;
            }
          }

          &[data-state='checked'] {
            border-color: vars.$color-brand-primary-900;
          }

          &[data-disabled] {
            cursor: not-allowed;
            border-color: vars.$color-neutral-gray-300;
            background-color: vars.$color-neutral-gray-100;
          }
        }

        &-inputWrapper {
          flex-direction: row-reverse;
          justify-content: space-between;
          width: 100%;
        }

        &-heading {
          @include text.text('sm', vars.$typography-weight-strong);
        }

        &-description {
          @include text.text('xs');
          margin-top: vars.$dimensions-d-1;
        }
      }
    }

  }
}
</style>
